import * as React from 'react';
import { Translate } from 'react-localize-redux';
import { PieChart } from 'react-minimal-pie-chart';
import { DataEntry } from 'react-minimal-pie-chart/types/commonTypes';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';
import { AAdditionalColumns } from '../../atoms/additionalColumns';
import { AAdditionalFieldColumns } from '../../atoms/additionalFieldColumns';
import { AAssortments } from '../../atoms/assortment';
import { ACalculatedFields } from '../../atoms/calculatedFields';
import { ACampaigns, Campaign, Period } from '../../atoms/campaignAtom';
import { getAvgColorByFrequencyScore, getEmojiByFrequencyScore } from '../../atoms/global/frequency';
import { PanelContext } from '../../components_v2/panel/context';
import { getPinColor } from '../../helpers/data/map';
import { PrimaryColor, GreenSidely, GreySidely, RedSidely, SidelyBlack, SteelBlueSidely, SurfaceColor } from '../../styles/global/css/Utils';
import { dayWord, translateToString } from '../../styles/global/translate';
import { CompanyStatus } from '../client-companies/model/Model';
import { Owner } from '../orders/model/Model';
import { HideTrashDiv } from '../products/style';
import { PinLassoPosition } from './lasso';
import { genAdditionalColumnClusterData, getStepColors, pickProductValue } from './mapAdditionalColumns';
import { AMapProduct } from '../../atoms/product';
import { MapContext } from './MapContext';
import { CompaniesInLasso, FetchKey, MinMax, MinMaxAdditionalColumn } from './MapView';
import { ClusterProperties, MapCompany, MapData, Point } from './model';
import MapToolTip from './toolTip';

export const DEFAULT_MAP_PIN_COLOR = '#83CEEE';
export const CHECKOUT_SUM_STEPS = 5;

export const dataFromDistribution = (distribution: number[], translate?: boolean): DataEntry[] => [
	{ value: distribution[0], color: GreenSidely, label: translate ? <Translate id='instore_presence' /> : 'instore_presence' },
	{ value: distribution[1], color: '#C8ED34', label: translate ? <Translate id={'order_in_progress'} /> : 'order_in_progress' },
	{ value: distribution[2], color: '#FFEA2C', label: translate ? <Translate id={'out_of_stock'} /> : 'out_of_stock' },
	{ value: distribution[3], color: '#FF7669', label: translate ? <Translate id={'de_listed'} /> : 'de_listed' },
	{ value: distribution[4], color: '#FFA340', label: translate ? <Translate id={'out_of_assortment'} /> : 'out_of_assortment' },
	{ value: distribution[5], color: PrimaryColor, label: translate ? <Translate id={'other'} /> : 'other' },
	{ value: distribution[6], color: '#BABABA', label: translate ? <Translate id={'uninformed'} /> : 'uninformed' }
];

export function getOngoingPeriod(periods: Period[], now: Date): Period | undefined {
	return periods.find(p => new Date(p.start) <= now && (!p.end || new Date(p.end) >= now));
}

export function getNextFuturePeriod(periods: Period[], now: Date): Period | undefined {
	return periods.filter(p => new Date(p.start) > now).sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())[0];
}

// a period without an explicit color still marks a real campaign: paint it
// blue rather than the "no campaign" grey
function getColorFromPeriods(periods: Period[]): string {
	const period = getOngoingPeriod(periods, new Date());
	return period ? period.color ?? PrimaryColor : DEFAULT_MAP_PIN_COLOR;
}

function getFutureColorFromPeriods(periods: Period[]): string {
	const period = getNextFuturePeriod(periods, new Date());
	return period ? period.color ?? PrimaryColor : DEFAULT_MAP_PIN_COLOR;
}

function campaignsClusterData(points: ClusterProperties[], campaigns: Record<number, Campaign>, future?: boolean): DataEntry[] {
	const counts = new Map<number, number>();

	for (const p of points) {
		const id = (future ? p.futureCampaign : p.campaign) ?? 0;
		counts.set(id, (counts.get(id) ?? 0) + 1);
	}

	return [...counts.entries()]
		.map(([campaignId, value]) => {
			const campaign = campaigns[campaignId];
			return {
				value,
				defaultCampaign: campaignId == 0,
				color: campaign?.name ? (future ? getFutureColorFromPeriods : getColorFromPeriods)(campaign?.periods ?? []) : DEFAULT_MAP_PIN_COLOR,
				label: campaign?.name ?? (future ? 'Pas de campagne à venir' : 'Pas de campagne en cours'),
			};
		})
		.sort((a, b) => a.defaultCampaign ? 1 : b.defaultCampaign ? -1 : b.value - a.value);
}

function statusClusterData(points: ClusterProperties[], statuses: Map<number, CompanyStatus>): DataEntry[] {
	const counts = new Map<number, number>();

	for (const p of points) {
		if (!p.status) continue;
		counts.set(p.status, (counts.get(p.status) ?? 0) + 1);
	}

	return [...counts.entries()]
		.map(([statusId, value]) => {
			const status = statuses.get(statusId);
			return {
				value,
				color: status?.color_code ?? DEFAULT_MAP_PIN_COLOR,
				label: status?.name ?? 'Pas de statut',
			};
		})
		.sort((a, b) => b.value - a.value);
}

function userClusterData(points: ClusterProperties[], users: Map<number, Owner>): DataEntry[] {
	const counts = new Map<number, number>();

	for (const p of points) {
		if (!p.owner) continue;
		counts.set(p.owner, (counts.get(p.owner) ?? 0) + 1);
	}

	return [...counts.entries()]
		.map(([ownerId, value]) => {
			const user = users.get(ownerId);
			return {
				value,
				color: user?.color ?? '',
				label: user?.name ?? '',
			};
		})
		.sort((a, b) => b.value - a.value);
}

function distributionClusterData(points: ClusterProperties[]): DataEntry[] {
	const stats = points.map(p => p.distribution ?? []).filter(e => e.length > 0);
	const added = stats.reduce((acc, p) => {
		return acc.map((y, i) => (p?.[i] ?? 0) + y);
	}, [0, 0, 0, 0, 0, 0, 0]);
	const max = stats.length * 100;
	return dataFromDistribution(added.map(e => e / max * 100), true);
}

function formsClusterData(points: ClusterProperties[], rangeColor: { firstRange: number, lastRange: number }): DataEntry[] {
	const t = points.reduce((acc: Record<string, { max: number, min: number, value: number }>, point) => {
		const color = getPinColorFromRange(point.formsRange, rangeColor);
		acc[color] ??= { max: 0, value: 0, min: Number.MAX_SAFE_INTEGER };
		acc[color].max = Math.max(acc[color].max, point.formsRange ?? 0);
		acc[color].min = Math.min(acc[color].min, point.formsRange ?? Number.MAX_SAFE_INTEGER);
		acc[color].value += 1;
		return acc;
	}, {});

	const obj = Object.entries(t).map(([color, { max, value, min }]): DataEntry => {
		let label = '';
		if (min === Number.MAX_SAFE_INTEGER) {
			label = translateToString('No Formbuilder');
		} else if (max === min) {
			label = `${max} ${dayWord(max)}`;
		} else {
			// the separator was the French word whichever language was reading it
			label = translateToString('map.legend.days_range', [['MIN', min.toString()], ['MAX', max.toString()]]);
		}
		return { label: label, color, value, max };
	}
	).filter(({ value }) => value !== 0).sort((a, b) => a.max - b.max);
	return obj;
}

function frequencyClusterData(points: ClusterProperties[], eventType: number): DataEntry[] {
	return points.reduce((acc: Array<{ value: number, color: string, label: string, score?: number | null }>, s: ClusterProperties) => {
		let label: string;
		let color: string;
		if (s.frequencies?.[eventType]?.score === undefined || s.frequencies?.[eventType]?.score === null) {
			label = translateToString('no_frequency');
			color = DEFAULT_MAP_PIN_COLOR;
		} else {
			label = getEmojiByFrequencyScore(s.frequencies[eventType]?.score ?? undefined);
			color = getAvgColorByFrequencyScore(s.frequencies[eventType]?.score ?? undefined) ?? DEFAULT_MAP_PIN_COLOR;
		}
		const index = acc.findIndex(a => a.color === color);
		if (index < 0) {
			acc.push({ value: 1, label, color, score: s.frequencies?.[eventType]?.score });
		} else {
			acc[index].value += 1;
		}
		return acc;
	}, [])
		.sort((a, b) => a.score && b.score ? b.score - a.score : a.score ? 1 : 0);
}

type T1 = { value: number, color: string, name?: string };
type T = { [key: number]: T1 } & { no_checkouts: T1 };
function checkoutSumClusterData(points: ClusterProperties[], checkoutsMinMax: MinMax): DataEntry[] {
	const div = (checkoutsMinMax.max - checkoutsMinMax.min) / CHECKOUT_SUM_STEPS;
	const data = points.reduce((acc: T, point) => {
		if (!point.checkouts || point.checkouts.currentSum === undefined || point.checkouts.currentSum === null) acc.no_checkouts.value += 1;
		else {
			const index = Math.floor(point.checkouts.currentSum / div);
			acc[index] ??= {
				value: 0,
				color: getStepColors({ r: 255, g: 255, b: 255 }, { r: 5, g: 119, b: 57 }, index, CHECKOUT_SUM_STEPS),
				name: `${(index * div).toFixed(2)} - ${((index + 1) * div).toFixed(2)}`
			};
			acc[index].value += 1;
		}
		return acc;
	}, { no_checkouts: { value: 0, color: DEFAULT_MAP_PIN_COLOR } });
	return Object.entries(data).map(([label, { color, value, name }]): DataEntry => ({ label: name ?? translateToString(label), color, value })).filter(({ value }) => value !== 0);
}

function checkoutClusterData(points: ClusterProperties[]): DataEntry[] {
	const data = points.reduce((acc, point) => {
		if (!point.checkouts || point.checkouts.evolution === undefined || point.checkouts.evolution === null) acc.no_checkouts.value += 1;
		else if (point.checkouts.evolution > 0) acc.positive_evolution.value += 1;
		else if (point.checkouts.evolution == 0) acc.no_changements.value += 1;
		else acc.negative_evolution.value += 1;
		return acc;
	}, { positive_evolution: { value: 0, color: GreenSidely }, negative_evolution: { value: 0, color: RedSidely }, no_changements: { value: 0, color: GreySidely }, no_checkouts: { value: 0, color: DEFAULT_MAP_PIN_COLOR } });
	return Object.entries(data).map(([label, { color, value }]): DataEntry => ({ label: translateToString(label), color, value })).filter(({ value }) => value !== 0);
}

export function getPinColorFromRange(range: number | undefined, rangeColor: { firstRange: number, lastRange: number }): string {
	const { firstRange, lastRange } = rangeColor;
	if (firstRange || lastRange) {
		const rumus1 = 100 / (lastRange - firstRange);
		if (range !== undefined && range !== null) {
			const rumus2 = rumus1 * range;
			return getPinColor(rumus2);
		}
	}
	return DEFAULT_MAP_PIN_COLOR;
}

export function handlePinColor(point: ClusterProperties | MapCompany, tabType: FetchKey, rangeColor: { firstRange: number, lastRange: number }, campaigns?: Record<number, Campaign>): string {
	const { firstRange, lastRange } = rangeColor;
	let range: number | undefined;


	switch (tabType) {
		case 'campaign':
			if ('campaignId' in point && point.campaignId && campaigns) {
				const campaign = campaigns[point.campaignId];
				return campaign?.name ? getColorFromPeriods(campaign?.periods ?? []) : DEFAULT_MAP_PIN_COLOR;
			}
			break;
		case 'campaign_future':
			if ('futureCampaignId' in point && point.futureCampaignId && campaigns) {
				const campaign = campaigns[point.futureCampaignId];
				return campaign?.name ? getFutureColorFromPeriods(campaign?.periods ?? []) : DEFAULT_MAP_PIN_COLOR;
			}
			break;
		case 'last':
			range = point.range !== undefined && point.range !== null ? point.range - firstRange : undefined;
			break;
		case 'next':
			range = point.nextRange !== undefined && point.nextRange !== null ? point.nextRange - firstRange : undefined;
			break;
		case 'orders':
			if ('orderRange' in point && point.orderRange !== undefined && point.orderRange !== null) {
				range = point.orderRange - firstRange;
			}
			break;
		case 'forms':
			if ('formsRange' in point && point.formsRange) {
				range = point.formsRange !== undefined && point.formsRange !== null ? point.formsRange - firstRange : undefined;
			} else {
				range = undefined;
			}
			break;
	}
	if (firstRange || lastRange) {
		const rumus1 = 100 / (lastRange - firstRange);
		if (range !== undefined && range !== null) {
			const rumus2 = rumus1 * range;
			return getPinColor(rumus2);
		} else {
			if (tabType === 'last') {
				if (!point.range && point.next === true) {
					return SteelBlueSidely;
				}
				return DEFAULT_MAP_PIN_COLOR;
			} else if (tabType === 'next') {
				if (!point.next) {
					return DEFAULT_MAP_PIN_COLOR;
				}
			}
		}
	}
	return DEFAULT_MAP_PIN_COLOR;
}

function DateClusterData(points: ClusterProperties[], tabType: FetchKey, rangeColor: { firstRange: number, lastRange: number }): DataEntry[] {
	const obj = points
		.map(point => handlePinColor(point, tabType, rangeColor))
		.reduce<{ [key: string]: number }>((acc, color: string) => {
			acc[color] ??= 0;
			acc[color] += 1;
			return acc;
		}, {});

	return Object.entries(obj).map(([key, value]) => ({ value, color: key }));
}
function clusterSizeFn(
	count: number,
	maxCount: number, // largest cluster currently on screen
	minSize = 24,
	maxSize = 72
): number {
	if (maxCount <= 1) return minSize;
	const t = Math.sqrt(count - 1) / Math.sqrt(maxCount - 1); // 0..1
	return minSize + t * (maxSize - minSize);
}

export default function ClusterIcon(props: {
	companiesInLasso: CompaniesInLasso,
	points: Point[],
	pointCount: number,
	properties: ClusterProperties[],
	tabType: FetchKey,
	rangeColor: { firstRange: number, lastRange: number },
	users: Map<number, Owner>,
	statuses: Map<number, CompanyStatus>,
	night?: boolean,
	eventType: number,
	onClusterClicked: (properties: ClusterProperties[]) => void,
	mapData: MapData | undefined,
	checkoutsMinMax?: MinMax,
	minMaxAdditionalColumn: MinMaxAdditionalColumn[]
	minMaxCalculatedFields: MinMaxAdditionalColumn[]
}) {
	const { hoveredPin, setHoveredPin } = React.useContext(MapContext);
	const [hoveredEvent] = React.useContext(PanelContext).calendarViewState.hoveredEventHook;
	const { points, pointCount, properties, users, statuses, night, tabType, rangeColor } = props;
	const additionalColumns = useRecoilValue(AAdditionalColumns);
	const additionalFieldColumns = useRecoilValue(AAdditionalFieldColumns);
	const calculatedFields = useRecoilValue(ACalculatedFields);
	const assortments = useRecoilValue(AAssortments);
	const campaigns = useRecoilValue(ACampaigns);
	const mapProduct = useRecoilValue(AMapProduct);
	const [showTooltip, setShowTooltip] = React.useState(false);

	let data: DataEntry[] = [];
	let noToolType = false;
	const { isOneChildSelected, isOneChildHovered } = props.properties.reduce((acc, c) => {
		if (!acc.isOneChildSelected) acc.isOneChildSelected = (hoveredEvent && hoveredEvent.companyId == c.id) || props.companiesInLasso.companies.some(c2 => c2.id === c.id);
		if (!acc.isOneChildHovered) acc.isOneChildHovered = typeof hoveredPin === 'object' ? hoveredPin.some(c2 => c2 === c.id) : c.id === hoveredPin;
		return acc;
	}, { isOneChildSelected: false, isOneChildHovered: false });
	if (typeof tabType === 'string') {
		switch (tabType) {
			case 'campaign':
			case 'campaign_future':
				data = campaignsClusterData(properties, campaigns, tabType === 'campaign_future');
				break;
			case 'status':
				data = statusClusterData(properties, statuses);
				break;
			case 'instore':
				data = distributionClusterData(properties);
				break;
			case 'per_users':
				data = userClusterData(properties, users);
				break;
			case 'checkouts_sum':
				if (!props.checkoutsMinMax) data = [];
				else data = checkoutSumClusterData(properties, props.checkoutsMinMax);
				break;
			case 'checkouts':
				data = checkoutClusterData(properties);
				break;
			case 'forms':
				data = formsClusterData(properties, rangeColor);
				break;
			case 'frequencies':
				data = frequencyClusterData(properties, props.eventType);
				break;
			case 'next':
			case 'last':
			case 'orders':
				data = DateClusterData(properties, tabType, rangeColor);
				noToolType = true;
				break;
		}
	} else if ('additionalColumn' in tabType) {
		const additionalColumn = additionalColumns.find(ac => ac.id == tabType.additionalColumn);
		if (additionalColumn) {
			const idString = additionalColumn.id.toString();
			const res = genAdditionalColumnClusterData(
				properties,
				(point) => point.additionalColumnsValue?.[idString],
				additionalColumn.type,
				rangeColor,
				assortments,
				props.minMaxAdditionalColumn,
				false,
				idString,
				additionalColumn?.display_options?.pin_styles,
				additionalColumn?.display_options,
			);
			data = res.data;
			noToolType = res.noToolType ?? false;
		}
	} else if ('additionalFieldColumn' in tabType) {
		const additionalColumn = additionalFieldColumns.find(afc => afc.field_id == tabType.additionalFieldColumn);
		if (additionalColumn) {
			const idString = additionalColumn.field_id.toString();
			const res = genAdditionalColumnClusterData(
				properties,
				(point) => point.additionalFieldColumnsValue?.[idString],
				additionalColumn.field_type,
				rangeColor,
				assortments,
				props.minMaxAdditionalColumn,
				true,
				idString,
				undefined,
				additionalColumn.display_options,
			);
			data = res.data;
			noToolType = res.noToolType ?? false;
		}
	} else if ('calculatedFieldColumn' in tabType) {
		const calculatedFieldColumn = calculatedFields.find(cf => cf.id == tabType.calculatedFieldColumn);
		if (calculatedFieldColumn) {
			const idString = calculatedFieldColumn.id.toString();
			const res = genAdditionalColumnClusterData(
				properties,
				(point) => pickProductValue(point.calculatedFieldColumnsValue?.[idString], mapProduct?.uuid),
				'CalculatedField',
				rangeColor,
				assortments,
				props.minMaxCalculatedFields,
				false,
				idString,
				undefined,
				calculatedFieldColumn.display_options,
			);
			data = res.data;
			noToolType = res.noToolType ?? false;
		}
	}

	const clusterSize = clusterSizeFn(pointCount, points.length, 15, 80);
	const pieSize = clusterSize + 10;

	return <HideTrashDiv
		style={{ cursor: 'pointer', width: pieSize, height: pieSize, translate: '-50% -50%' }}
		hoverStyle='z-index: 1;'
		hoverTrashStyle='z-index: 1;'
		onClick={() => {
			props.onClusterClicked(props.properties);
		}}
		onMouseEnter={() => {
			setShowTooltip(true);
			return isOneChildSelected && setHoveredPin(properties.reduce((acc, p) => p.id ? [...acc, p.id] : acc, []));
		}}
		onMouseLeave={() => {
			setShowTooltip(true);
			return isOneChildSelected && setHoveredPin(undefined);
		}}
	>
		{data.length > 0 &&
			<PieChart
				className='circle'
				style={{ position: 'absolute', left: '50%', top: '50%', width: pieSize, height: pieSize, transform: 'translate(-50%, -50%)' }}
				lineWidth={70}
				center={[50, 50]}
				viewBoxSize={[100, 100]}
				data={data}
			/>
		}
		<div
			className='bg-cluster'
			style={{ height: '100%', width: '100%' }}
		>
			<ClusterDiv
				className='cluster-marker'
				size={clusterSize}
			>
				{pointCount}
				<ClusterBorder size={pieSize} />
				{isOneChildSelected && <PinLassoPosition
					style={{
						top: '0',
						right: '0',
						translateValue: '50% -50%',
						size: '10px'
					}}
					selected={isOneChildHovered}
				/>}
			</ClusterDiv>
		</div>
		{showTooltip && !noToolType && data.every(e => !isNaN(e.value)) && <MapToolTip data={data} instore={tabType == 'instore'} night={night} cluster />}
	</HideTrashDiv>;
}

const ClusterDiv = styled.div<{ size: number }>`
	width: ${({ size }) => `${size}px`};
	height: ${({ size }) => `${size}px`};
	background-color: ${SurfaceColor};
	color: ${SidelyBlack};
`;


const ClusterBorder = styled.div<{ size: number }>`
	width: ${({ size }) => size}px;
	height: ${({ size }) => size}px;
	border: 2px solid white;
	position: absolute;
	border-radius: 100%;
`;
